import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CheckCircle, Clock, Eye, Home, Shield, User, ArrowRight, Star, MapPin } from 'lucide-react';

const VaaSTenantPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900/50 opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              For Tenants
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Find Your Perfect Home
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Browse properties, view 360° tours, and book viewings with ease. 
              Our streamlined process gets you into your new home faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/properties"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold transition-all"
              >
                Browse Properties
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-xl font-semibold transition-all"
              >
                Create Profile
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">Your Journey to a New Home</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Our profile-first approach ensures a smoother experience for everyone
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {[
            {
              step: "1",
              icon: User,
              title: "Create Profile",
              description: "Sign up and build your tenant profile"
            },
            {
              step: "2",
              icon: Shield,
              title: "Get Verified",
              description: "Complete verification to boost your score"
            },
            {
              step: "3",
              icon: Home,
              title: "Browse",
              description: "Explore properties with 360° tours"
            },
            {
              step: "4",
              icon: Calendar,
              title: "Book Viewing",
              description: "Schedule in-person or virtual viewing"
            },
            {
              step: "5",
              icon: CheckCircle,
              title: "Apply",
              description: "Submit application with priority"
            }
          ].map((item, index) => (
            <div key={index} className="relative">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 h-full text-center">
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-blue-400" />
                </div>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
              {index < 4 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 text-slate-600">
                  <ArrowRight className="w-6 h-6" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Why Verify First */}
      <div className="bg-slate-800/30 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Why Verify First?</h2>
              <p className="text-slate-300 mb-6">
                Our profile-first approach benefits you. By verifying your details upfront, 
                you&apos;ll get priority access to viewings and faster application processing.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: "Faster Applications",
                    description: "Your verified info auto-fills applications - submit in minutes."
                  },
                  {
                    title: "Higher Priority",
                    description: "Verified tenants get priority in viewing queues and applications."
                  },
                  {
                    title: "No Time Wasters",
                    description: "Landlords trust verified profiles, leading to quicker decisions."
                  },
                  {
                    title: "Better Matches",
                    description: "Our scoring system matches you with suitable properties."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-white font-medium">{item.title}</h3>
                      <p className="text-slate-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl p-8 border border-blue-500/20">
              <h3 className="text-xl font-semibold text-white mb-6">What You&apos;ll Need</h3>
              <ul className="space-y-4">
                {[
                  "Valid ID (passport or driving licence)",
                  "Proof of income or employment",
                  "Previous landlord reference",
                  "Right to Rent documentation (if applicable)"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 text-sm font-medium">
                      {index + 1}
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-700/50">
                <p className="text-slate-400 text-sm">
                  Don&apos;t have everything? Start anyway - our system guides you through verification 
                  and only requires what&apos;s needed for each property.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Viewing Options */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">Viewing Options</h2>
          <p className="text-slate-400">Choose how you want to see properties</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: Eye,
              title: "360° Virtual Tour",
              description: "Explore every room from home. Available on all properties with our LiDAR scanning.",
              available: "Always available",
              color: "purple"
            },
            {
              icon: Home,
              title: "In-Person Viewing",
              description: "Book a slot to visit the property. Meet the landlord or our VaaS team.",
              available: "Subject to availability",
              color: "blue"
            },
            {
              icon: Calendar,
              title: "Remote Video Viewing",
              description: "Live video call with someone at the property. Perfect for relocating tenants.",
              available: "Where offered",
              color: "emerald"
            }
          ].map((option, index) => (
            <div 
              key={index} 
              className={`bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-${option.color}-500/50 transition-all`}
            >
              <div className={`w-14 h-14 bg-${option.color}-500/10 rounded-xl flex items-center justify-center mb-4`}>
                <option.icon className={`w-7 h-7 text-${option.color}-400`} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{option.title}</h3>
              <p className="text-slate-400 mb-4">{option.description}</p>
              <span className={`text-${option.color}-400 text-sm font-medium`}>{option.available}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Waitlist & Priority */}
      <div className="bg-slate-800/30 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Can&apos;t Find a Slot?</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Join the waitlist and we&apos;ll notify you instantly when a viewing becomes available
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Waitlist Priority</h3>
              <p className="text-slate-400 mb-4">Your position is based on:</p>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Profile verification score
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Viewing history (attendance rate)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  Time on waitlist
                </li>
              </ul>
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Favourite Properties</h3>
              <p className="text-slate-400 mb-4">
                Save properties you&apos;re interested in and we&apos;ll automatically notify you when:
              </p>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  New viewing slots become available
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  Price changes
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-purple-400" />
                  Similar properties are listed
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback After Viewing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">After Your Viewing</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Your feedback helps you and helps others
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl p-8 border border-blue-500/20">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Tell Us What You Think</h3>
              <p className="text-slate-300">
                After each viewing, we&apos;ll ask for quick feedback:
              </p>
              <ul className="mt-4 space-y-2 text-slate-400">
                <li>• Still interested?</li>
                <li>• Was it as described?</li>
                <li>• Would you recommend?</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Why It Matters</h3>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <span>Expressing interest boosts your application priority</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <span>Landlords see engaged tenants first</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <span>Feedback improves listings for everyone</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find Your New Home?</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Create your profile today and start browsing hundreds of verified properties across Scotland.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all"
            >
              Create Free Profile
            </Link>
            <Link
              to="/properties"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-700/50 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              Browse Properties
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-center text-slate-500 text-sm">
          Viewing availability depends on landlord preferences. Some properties may only offer 
          virtual tours or require applications before viewing.
        </p>
      </div>
    </div>
  );
};

export default VaaSTenantPage;
